import React, { FC, useEffect } from 'react';
import Modal from 'react-modal';
import { PrimaryButton, SecondaryButton, ToggleButton } from '..';
import { Input } from '../shared/custom-inputs';
import { CheckBox } from '../shared/custom-inputs/atom/checkbox';
import { useAppSelector, useAppDispatch } from '../../../hooks/redux.hooks';
import { kitchensSelector, getAllKitchens } from '../../../store/slices';
import { DeliveryRateResponse } from '../../../types/delivery-rate.types';
import { useKitchenConfig } from '../../../hooks';
import { KitchenDeliveryConfig } from '../../../types/kitchen.types';
import { kitchenTypes } from '../../../constants';

// Simple Toggle component just for this form
const Toggle: FC<{
  isEnabled: boolean;
  onChange: (value: boolean) => void;
  label: string;
}> = ({ isEnabled, onChange, label }) => {
  return (
    <div className="flex flex-col">
      <label className="text-xs semibold leading-7 text-neutral-900 mb-1">
        {label}
      </label>
      <button
        type="button"
        className={`h-8 w-16 px-1 flex rounded-full items-center transition duration-300 relative ${
          isEnabled ? 'bg-primary-700' : 'bg-neutral-400'
        }`}
        onClick={() => onChange(!isEnabled)}
      >
        {!isEnabled && (
          <p className="absolute top-1.5 right-2 text-sm semibold">NO</p>
        )}
        <div
          className={`h-5 w-5 rounded-full bg-white transition duration-300 ${
            isEnabled ? 'translate-x-9' : 'translate-x-0'
          }`}
        />
        {isEnabled && (
          <p className="absolute top-1.5 left-2 text-sm semibold text-white">
            YES
          </p>
        )}
      </button>
    </div>
  );
};

export const CreateKitchenConfigModal: FC<{
  isOpen: boolean;
  onClose: () => void;
  deliveryRates: DeliveryRateResponse[];
  selectedConfig: KitchenDeliveryConfig | null;
}> = ({ isOpen, onClose, deliveryRates, selectedConfig }) => {
  const dispatch = useAppDispatch();
  const { kitchens } = useAppSelector(kitchensSelector);
  const {
    formData,
    isSubmitting,
    activeKitchenType,
    setFormData,
    handleSubmit,
    handleDayChange,
    handleInputChange,
    handleAddTimeSurge,
    setActiveKitchenType,
    handleTimeSurgeChange,
    handleRemoveTimeSurge,
    handleAddDeliveryRate,
    handleDeliveryRateChange,
    handleRemoveDeliveryRate,
    handleBooleanSelectChange,
  } = useKitchenConfig(onClose, deliveryRates);

  // Helper function to convert boolean to select value
  const booleanToSelect = (value: boolean) => (value ? 'Yes' : 'No');

  // Helper function to handle toggle changes
  const handleToggleChange = (name: string, value: boolean) => {
    const event = {
      target: {
        name,
        value: value ? 'Yes' : 'No',
      },
    } as React.ChangeEvent<HTMLSelectElement>;

    handleBooleanSelectChange(event);
  };

  const daysOfWeek = [
    { id: 0, name: 'Sunday' },
    { id: 1, name: 'Monday' },
    { id: 2, name: 'Tuesday' },
    { id: 3, name: 'Wednesday' },
    { id: 4, name: 'Thursday' },
    { id: 5, name: 'Friday' },
    { id: 6, name: 'Saturday' },
  ];

  // Prefill form when in edit mode
  useEffect(() => {
    if (selectedConfig) {
      setFormData({
        kitchenCode: selectedConfig.kitchen.code,
        isEnabled: selectedConfig.isEnabled,
        enableSurge: selectedConfig.enableSurge,
        surgeMultiplier: selectedConfig.surgeMultiplier,
        enableWeatherPricing: selectedConfig.enableWeatherPricing,
        weatherMultiplier: selectedConfig.weatherMultiplier,
        customDeliveryRates: selectedConfig.customDeliveryRates.map((rate) => ({
          deliveryRateId: rate.deliveryRateId,
          minPrice: parseFloat(rate.minPrice),
          maxPrice: parseFloat(rate.maxPrice),
          rate: parseFloat(rate.rate),
        })),
        timeSurgePricing: selectedConfig.timeSurgePricing.map((surge) => ({
          startTime: surge.startTime,
          endTime: surge.endTime,
          multiplier: surge.multiplier,
          daysOfWeek: [...surge.daysOfWeek],
        })),
        id: selectedConfig.id,
      });
    } else {
      setFormData({
        kitchenCode: '',
        isEnabled: false,
        enableSurge: false,
        description: '',
        surgeMultiplier: 1.5,
        enableWeatherPricing: false,
        weatherMultiplier: 1.3,
        customDeliveryRates: [],
        timeSurgePricing: [],
      });
    }
  }, [selectedConfig, setFormData]);

  // Update title based on edit/create mode
  const modalTitle = selectedConfig
    ? 'Edit Kitchen Configuration'
    : 'Setup Kitchen Configuration';

  return (
    <Modal
      isOpen={isOpen}
      ariaHideApp={false}
      onRequestClose={onClose}
      overlayClassName="modal-overlay"
      className={`w-11/12 sm:w-6/12 h-10/12 default-modal`}
    >
      <div className="p-6 bg-white overflow-y-auto h-full">
        <div className="mb-6">
          <h2 className="text-xl font-bold opacity-80">{modalTitle}</h2>
          <p className="text-gray-500">
            Configure delivery settings for a kitchen
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div className="flex flex-col gap-6">
              <div className="bg-gray-50 p-4 rounded-md">
                <h3 className="text-base font-bold mb-1">
                  Basic Configuration
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Configure the kitchen and basic settings.
                </p>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-sm semibold -mb-3 mt-5">Kitchen type</p>
                    <div className="flex space-x-2">
                      {kitchenTypes.map(({ name, value }) => (
                        <ToggleButton
                          text={name}
                          key={value}
                          onClick={() => {
                            setFormData({
                              ...formData,
                              kitchenCode: '',
                            });
                            setActiveKitchenType(value);
                            if (value === 'shadow') {
                              dispatch(getAllKitchens({ isShadow: 'true' }));
                            } else {
                              dispatch(getAllKitchens());
                            }
                          }}
                          active={activeKitchenType === value}
                        />
                      ))}
                    </div>
                  </div>
                  <div />
                  <div>
                    <label className="text-xs semibold leading-7 text-neutral-900">
                      Kitchen
                    </label>
                    <select
                      name="kitchenCode"
                      value={formData.kitchenCode}
                      onChange={handleInputChange}
                      className="w-full p-3 border border-gray-300 rounded-[4px] focus:outline-none focus:ring-4 focus:ring-gray-300 focus:ring-opacity-30 focus:border-gray-500"
                      required
                    >
                      <option value="">Select a kitchen</option>
                      {kitchens
                        .filter((kitchen) => {
                          if (activeKitchenType === 'shadow') return true; // already filtered by API
                          if (activeKitchenType === '500chow')
                            return !kitchen.isVendorEnabled;
                          return kitchen.isVendorEnabled;
                        })
                        .map((kitchen) => (
                          <option key={kitchen.id} value={kitchen.code}>
                            {kitchen.name}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs semibold leading-7 text-neutral-900">
                      Enabled
                    </label>
                    <select
                      name="isEnabled"
                      value={booleanToSelect(formData.isEnabled)}
                      onChange={handleBooleanSelectChange}
                      className="w-full p-3 border border-gray-300 rounded-[4px] focus:outline-none focus:ring-4 focus:ring-gray-300 focus:ring-opacity-30 focus:border-gray-500"
                    >
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                </div>

                {/* <div className="mb-4">
                  <Input
                    label="Description"
                    name="description"
                    type="text"
                    value={formData.description || ''}
                    onChange={handleInputChange}
                  />
                </div> */}
              </div>

              <div className="bg-gray-50 p-4 rounded-md">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-base font-bold">Custom Delivery Rates</h3>
                  <button
                    type="button"
                    className="px-3 py-1 bg-blue-500 text-white rounded-md text-sm"
                    onClick={handleAddDeliveryRate}
                  >
                    Add Rate
                  </button>
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  Configure custom delivery rates for this kitchen.
                </p>

                {formData.customDeliveryRates.length > 0 ? (
                  formData.customDeliveryRates.map((rate, index) => (
                    <div
                      key={index}
                      className="mb-4 p-3 border border-gray-200 rounded-md"
                    >
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="text-sm font-semibold">
                          Rate #{index + 1}
                        </h4>
                        <button
                          type="button"
                          className="text-red-500 text-sm"
                          onClick={() => handleRemoveDeliveryRate(index)}
                        >
                          Remove
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mb-3">
                        <div>
                          <label className="text-xs semibold leading-7 text-neutral-900">
                            Delivery Rate
                          </label>
                          <select
                            name="deliveryRateId"
                            value={rate.deliveryRateId || ''}
                            onChange={(e) => handleDeliveryRateChange(e, index)}
                            className="w-full p-3 border border-gray-300 rounded-[4px] focus:outline-none focus:ring-4 focus:ring-gray-300 focus:ring-opacity-30 focus:border-gray-500"
                            required
                          >
                            <option value="">Select a delivery rate</option>
                            {deliveryRates.map((dr) => (
                              <option key={dr.id} value={dr.id}>
                                {dr.minDistance}km-{dr.maxDistance}km - ₦{dr.minPrice}-₦{dr.maxPrice}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Input
                            label="Min Price (₦)"
                            name="minPrice"
                            type="number"
                            value={rate.minPrice}
                            onChange={(e) => handleDeliveryRateChange(e, index)}
                            required
                          />
                        </div>
                        <div>
                          <Input
                            label="Max Price (₦)"
                            name="maxPrice"
                            type="number"
                            value={rate.maxPrice}
                            onChange={(e) => handleDeliveryRateChange(e, index)}
                            required
                          />
                        </div>
                        <div>
                          <Input
                            label="Rate"
                            name="rate"
                            type="number"
                            value={rate.rate}
                            onChange={(e) => handleDeliveryRateChange(e, index)}
                            required
                          />
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-400 italic">
                    No custom delivery rates added yet.
                  </p>
                )}
              </div>

              <div className="bg-gray-50 p-4 rounded-md">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-base font-bold mb-1">
                      Weather Configuration
                    </h3>
                    <p className="text-sm text-gray-500">
                      Configure weather-based pricing settings.
                    </p>
                  </div>
                  <Toggle
                    label=""
                    isEnabled={formData.enableWeatherPricing}
                    onChange={(value) =>
                      handleToggleChange('enableWeatherPricing', value)
                    }
                  />
                </div>

                {formData.enableWeatherPricing && (
                  <div className="mt-4">
                    <div className="grid grid-cols-1 gap-4 mb-4">
                      <div>
                        <Input
                          label="Weather Multiplier"
                          name="weatherMultiplier"
                          type="number"
                          step="0.1"
                          value={formData.weatherMultiplier}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-gray-50 p-4 rounded-md">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-base font-bold">Time Surge Pricing</h3>
                  <button
                    type="button"
                    className="px-3 py-1 bg-blue-500 text-white rounded-md text-sm"
                    onClick={handleAddTimeSurge}
                  >
                    Add Time Surge
                  </button>
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  Configure time-based surge pricing.
                </p>

                {formData.timeSurgePricing.length > 0 ? (
                  formData.timeSurgePricing.map((surge, index) => (
                    <div
                      key={index}
                      className="mb-4 p-3 border border-gray-200 rounded-md"
                    >
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="text-sm font-semibold">
                          Time Surge #{index + 1}
                        </h4>
                        <button
                          type="button"
                          className="text-red-500 text-sm"
                          onClick={() => handleRemoveTimeSurge(index)}
                        >
                          Remove
                        </button>
                      </div>
                      <div className="grid grid-cols-3 gap-4 mb-3">
                        <div>
                          <Input
                            label="Start Time"
                            name="startTime"
                            type="time"
                            value={surge.startTime}
                            onChange={(e) => handleTimeSurgeChange(e, index)}
                            required
                          />
                        </div>
                        <div>
                          <Input
                            label="End Time"
                            name="endTime"
                            type="time"
                            value={surge.endTime}
                            onChange={(e) => handleTimeSurgeChange(e, index)}
                            required
                          />
                        </div>
                        <div>
                          <Input
                            label="Multiplier"
                            name="multiplier"
                            type="number"
                            step="0.1"
                            value={surge.multiplier}
                            onChange={(e) => handleTimeSurgeChange(e, index)}
                            required
                          />
                        </div>
                      </div>
                      <div className="mb-2">
                        <label className="text-xs semibold leading-7 text-neutral-900 block mb-2">
                          Days of Week
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {daysOfWeek.map((day) => (
                            <div key={day.id} className="flex items-center">
                              <CheckBox
                                id={`day-${index}-${day.id}`}
                                type="checkbox"
                                checked={surge.daysOfWeek.includes(day.id)}
                                onChange={() => handleDayChange(index, day.id)}
                                label={day.name}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-400 italic">
                    No time surge pricing added yet.
                  </p>
                )}
              </div>

              <div className="bg-gray-50 p-4 rounded-md">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-base font-bold mb-1">
                      Surge Configuration
                    </h3>
                    <p className="text-sm text-gray-500">
                      Configure surge pricing settings.
                    </p>
                  </div>
                  <Toggle
                    label=""
                    isEnabled={formData.enableSurge}
                    onChange={(value) =>
                      handleToggleChange('enableSurge', value)
                    }
                  />
                </div>

                {formData.enableSurge && (
                  <div className="mt-4">
                    <div className="grid grid-cols-1 gap-4 mb-4">
                      <div>
                        <Input
                          label="Surge Multiplier"
                          name="surgeMultiplier"
                          type="number"
                          step="0.1"
                          value={formData.surgeMultiplier}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end mt-6 space-x-4">
            <SecondaryButton text="Cancel" onClick={onClose} />
            <PrimaryButton
              text={
                selectedConfig ? 'Update Configuration' : 'Save Configuration'
              }
              type="submit"
              disabled={isSubmitting}
            />
          </div>
        </form>
      </div>
    </Modal>
  );
};
